import {
  ArrowLeft as ArrowLeftGlyph,
  Calendar as CalendarGlyph,
  Check as CheckGlyph,
  ChevronDown as ChevronDownGlyph,
  ChevronLeft as ChevronLeftGlyph,
  ChevronRight as ChevronRightGlyph,
  CircleAlert,
  CircleCheck,
  Clock as ClockGlyph,
  House,
  LayoutGrid,
  Printer,
  RefreshCw,
  Repeat as RepeatGlyph,
  Server as ServerGlyph,
  Sparkles as SparklesGlyph,
  SquarePen,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  UserRound,
  Ellipsis,
  ExternalLink as ExternalLinkGlyph,
  Info as InfoGlyph,
  KeyRound,
  Link2Off,
  Lock as LockGlyph,
  PencilLine,
  Search as SearchGlyph,
  Plus as PlusGlyph,
  Send,
  ServerCrash,
  TriangleAlert,
  WifiOff,
  X,
  type LucideIcon,
  type LucideProps,
} from "lucide-react";

/**
 * Lucide glyphs (ISC licence) behind the names the kit already uses, so a
 * swap of the set never touches a call site. Every icon strokes
 * `currentColor` and inherits the surrounding token colour; the design sizes
 * them at 16, 18 or 20 px.
 *
 * Icons are decorative by default (`aria-hidden`); the accessible name belongs
 * on the control that wraps them.
 */
export type IconProps = LucideProps;

function Icon({
  glyph: Glyph,
  size = 16,
  strokeWidth = 1.8,
  className,
  ...props
}: IconProps & { glyph: LucideIcon }) {
  return (
    <Glyph
      size={size}
      strokeWidth={strokeWidth}
      aria-hidden="true"
      focusable="false"
      className={className ? `shrink-0 ${className}` : "shrink-0"}
      {...props}
    />
  );
}

export const ChevronDown = (props: IconProps) => (
  <Icon glyph={ChevronDownGlyph} {...props} />
);
export const ChevronRight = (props: IconProps) => (
  <Icon glyph={ChevronRightGlyph} {...props} />
);
export const ChevronLeft = (props: IconProps) => (
  <Icon glyph={ChevronLeftGlyph} {...props} />
);
export const ArrowLeft = (props: IconProps) => (
  <Icon glyph={ArrowLeftGlyph} {...props} />
);
export const Check = (props: IconProps) => (
  <Icon glyph={CheckGlyph} {...props} />
);
export const Plus = (props: IconProps) => <Icon glyph={PlusGlyph} {...props} />;
export const Close = (props: IconProps) => <Icon glyph={X} {...props} />;
export const Alert = (props: IconProps) => (
  <Icon glyph={CircleAlert} {...props} />
);
/** Immutable System Ability marker. */
export const Lock = (props: IconProps) => <Icon glyph={LockGlyph} {...props} />;
export const ExternalLink = (props: IconProps) => (
  <Icon glyph={ExternalLinkGlyph} {...props} />
);
export const Calendar = (props: IconProps) => (
  <Icon glyph={CalendarGlyph} {...props} />
);
export const Clock = (props: IconProps) => (
  <Icon glyph={ClockGlyph} {...props} />
);
export const Success = (props: IconProps) => (
  <Icon glyph={CircleCheck} {...props} />
);
export const Info = (props: IconProps) => <Icon glyph={InfoGlyph} {...props} />;
export const Warning = (props: IconProps) => (
  <Icon glyph={TriangleAlert} {...props} />
);
export const Draft = (props: IconProps) => (
  <Icon glyph={PencilLine} {...props} />
);
export const More = (props: IconProps) => <Icon glyph={Ellipsis} {...props} />;
export const Offline = (props: IconProps) => (
  <Icon glyph={WifiOff} {...props} />
);
export const ServerDown = (props: IconProps) => (
  <Icon glyph={ServerCrash} {...props} />
);
export const LinkExpired = (props: IconProps) => (
  <Icon glyph={Link2Off} {...props} />
);
export const SignIn = (props: IconProps) => (
  <Icon glyph={KeyRound} {...props} />
);
export const Telegram = (props: IconProps) => <Icon glyph={Send} {...props} />;

export const Search = (props: IconProps) => (
  <Icon glyph={SearchGlyph} {...props} />
);

export const Home = (props: IconProps) => <Icon glyph={House} {...props} />;
export const Grid = (props: IconProps) => (
  <Icon glyph={LayoutGrid} {...props} />
);
export const Sparkles = (props: IconProps) => (
  <Icon glyph={SparklesGlyph} {...props} />
);
export const User = (props: IconProps) => <Icon glyph={UserRound} {...props} />;
export const Print = (props: IconProps) => <Icon glyph={Printer} {...props} />;
export const Edit = (props: IconProps) => <Icon glyph={SquarePen} {...props} />;
export const Trash = (props: IconProps) => <Icon glyph={Trash2} {...props} />;
export const Repeat = (props: IconProps) => (
  <Icon glyph={RepeatGlyph} {...props} />
);
export const Refresh = (props: IconProps) => (
  <Icon glyph={RefreshCw} {...props} />
);
export const ThumbUp = (props: IconProps) => (
  <Icon glyph={ThumbsUp} {...props} />
);
export const ThumbDown = (props: IconProps) => (
  <Icon glyph={ThumbsDown} {...props} />
);
export const Server = (props: IconProps) => (
  <Icon glyph={ServerGlyph} {...props} />
);

/** Indeterminate progress ring for the startup sequence. */
export function Spinner({
  size = 16,
  label,
}: {
  size?: number;
  label: string;
}) {
  return (
    <span role="status" aria-live="polite" className="inline-flex">
      <span
        aria-hidden="true"
        className="block animate-spin rounded-full border-2 border-line-subtle border-t-accent"
        style={{ width: size, height: size }}
      />
      <span className="sr-only">{label}</span>
    </span>
  );
}
